/** Facilities and maintenance: API types, labels, and shared queries. */
import { useQuery } from '@tanstack/react-query';
import { api, apiUrl, upload } from './api';
import type { FacilitiesRole, Page, RecurrenceFrequency, Ref, TenantRole, WorkOrderPriority, WorkOrderSource, WorkOrderStatus } from './types';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type FacilitySystem =
  | 'HVAC'
  | 'ELECTRICAL'
  | 'PLUMBING'
  | 'ROOFING'
  | 'FIRE_LIFE_SAFETY'
  | 'SECURITY'
  | 'ELEVATOR'
  | 'KITCHEN'
  | 'PLAYGROUND'
  | 'GROUNDS'
  | 'STRUCTURAL'
  | 'FURNISHINGS'
  | 'OTHER';
export type EquipmentStatus = 'IN_SERVICE' | 'NEEDS_REPAIR' | 'OUT_OF_SERVICE' | 'RETIRED';
export type FacilityCondition = 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'CRITICAL';
export type FacilityLogKind = 'MAINTENANCE' | 'REPAIR' | 'INSPECTION' | 'REPLACEMENT' | 'INSTALLATION' | 'NOTE';
export type FacilityScheduleKind = 'PREVENTIVE_MAINTENANCE' | 'INSPECTION';
export type InspectionKind = 'CUSTODIAL_WALKTHROUGH' | 'SAFETY_AUDIT' | 'PLAYGROUND' | 'FIRE_SAFETY' | 'OTHER';
export type InspectionStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELED';
export type InspectionResult = 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
export type WorkOrderCostKind = 'MATERIALS' | 'CONTRACTOR' | 'EQUIPMENT' | 'OTHER';
export type CostKind = 'LABOR' | WorkOrderCostKind | 'RECORDED';
export type FacilityDocumentKind = 'MANUAL' | 'WARRANTY' | 'MAINTENANCE_LOG' | 'INSPECTION_REPORT' | 'CONTRACT' | 'PERMIT' | 'DRAWING' | 'OTHER';
export type RoutingAssignment = 'USER' | 'ROUND_ROBIN' | 'TEAM_LEAD';
export type ScheduleState = 'PAUSED' | 'OVERDUE' | 'OPEN' | 'ENDED' | 'OPENING' | 'SCHEDULED';

// ---------------------------------------------------------------------------
// Places and equipment
// ---------------------------------------------------------------------------

/** GET /facilities/places: buildings open today, with their rooms. */
export interface FacilityPlace {
  id: string;
  name: string;
  rooms: Ref[];
}

export interface EquipmentRef {
  id: string;
  code: string | null;
  name: string;
  system: FacilitySystem;
}

/** GET /facilities/equipment-options */
export interface EquipmentOption extends EquipmentRef {
  buildingId: string | null;
  roomId: string | null;
}

export interface Lifecycle {
  ageYears: number | null;
  replacementYear: number | null;
  /** Negative once the equipment is past its expected life. */
  remainingYears: number | null;
}

export interface Equipment extends Lifecycle {
  id: string;
  code: string | null;
  name: string;
  system: FacilitySystem;
  status: EquipmentStatus;
  condition: FacilityCondition;
  buildingId: string | null;
  roomId: string | null;
  locationDetail: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  vendor: string | null;
  /** YYYY-MM-DD */
  installedOn: string | null;
  expectedLifeYears: number | null;
  purchaseCost: number | null;
  replacementCost: number | null;
  warrantyExpiresOn: string | null;
  notes: string | null;
  retiredOn: string | null;
  createdAt: string;
  updatedAt: string;
  building: Ref | null;
  room: Ref | null;
}

/** GET /facilities/assets (paged) */
export interface EquipmentListItem extends Equipment {
  openWorkOrders: number;
  nextMaintenanceDue: string | null;
}

export interface EquipmentHistoryEntry {
  id: string;
  kind: FacilityLogKind;
  performedOn: string;
  summary: string;
  details: string | null;
  cost: number | null;
  vendor: string | null;
  workOrderId: string | null;
  createdById: string | null;
  createdByName: string;
  createdAt: string;
  workOrder: { id: string; number: number; title: string; status: WorkOrderStatus } | null;
}

export interface DocumentSummary {
  id: string;
  title: string;
  kind: FacilityDocumentKind;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

/** GET /facilities/assets/:id */
export interface EquipmentDetail extends Equipment {
  history: EquipmentHistoryEntry[];
  schedules: {
    id: string;
    kind: FacilityScheduleKind;
    name: string;
    enabled: boolean;
    frequency: RecurrenceFrequency;
    interval: number;
    nextDueOn: string | null;
    template: Ref | null;
  }[];
  documents: DocumentSummary[];
  inspections: { id: string; name: string; status: InspectionStatus; dueOn: string | null; completedAt: string | null; score: number | null; failedCount: number }[];
  openWorkOrders: {
    id: string;
    number: number;
    title: string;
    status: WorkOrderStatus;
    priority: WorkOrderPriority;
    dueAt: string | null;
    source: WorkOrderSource;
    assignedTo: Ref | null;
  }[];
  costs: { laborMinutes: number; hourlyRate: number | null; laborCost: number | null; otherCosts: number | null; total: number | null; finishedWorkOrders: number };
  permissions: { edit: boolean; delete: boolean };
}

/** POST /facilities/assets, PATCH /facilities/assets/:id */
export interface EquipmentInput {
  code?: string | null;
  name?: string;
  system?: FacilitySystem;
  status?: EquipmentStatus;
  condition?: FacilityCondition;
  buildingId?: string | null;
  roomId?: string | null;
  locationDetail?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  vendor?: string | null;
  installedOn?: string | null;
  expectedLifeYears?: number | null;
  purchaseCost?: number | null;
  replacementCost?: number | null;
  warrantyExpiresOn?: string | null;
  notes?: string | null;
}

/** POST /facilities/assets/:id/history */
export interface HistoryEntryInput {
  kind: FacilityLogKind;
  performedOn?: string;
  summary: string;
  details?: string | null;
  cost?: number | null;
  vendor?: string | null;
  /** A replacement or installation starts the equipment's life over from that day. */
  restartLife?: boolean;
}

// ---------------------------------------------------------------------------
// Schedules
// ---------------------------------------------------------------------------

export interface ScheduleOccurrence {
  type: 'WORK_ORDER' | 'INSPECTION';
  id: string;
  number: number | null;
  title: string;
  status: string;
  dueAt: string | null;
  dueOn: string | null;
  assignedTo: Ref | null;
}

export interface FacilitySchedule {
  id: string;
  kind: FacilityScheduleKind;
  name: string;
  description: string | null;
  enabled: boolean;
  facilityAssetId: string | null;
  buildingId: string | null;
  roomId: string | null;
  departmentId: string | null;
  priority: WorkOrderPriority;
  checklist: string[];
  estimatedMinutes: number | null;
  templateId: string | null;
  assignedToId: string | null;
  frequency: RecurrenceFrequency;
  interval: number;
  weekdays: number[];
  dayOfMonth: number | null;
  month: number | null;
  startDate: string;
  endDate: string | null;
  leadDays: number;
  nextDueOn: string | null;
  lastOpenedAt: string | null;
  /** Occurrences skipped because the one before was still open. */
  missedCount: number;
  createdAt: string;
  updatedAt: string;
  facilityAsset: EquipmentRef | null;
  building: Ref | null;
  room: Ref | null;
  department: Ref | null;
  template: { id: string; name: string; kind: InspectionKind } | null;
  assignedTo: Ref | null;
  /** When the next occurrence opens: its due date less the lead days. */
  opensOn: string | null;
  /** "Every 3 months on the 15th" */
  recurrence: string;
  openOccurrence: ScheduleOccurrence | null;
  state: ScheduleState;
}

/** GET /facilities/schedules/:id */
export interface FacilityScheduleDetail extends FacilitySchedule {
  upcoming: string[];
}

/** GET /facilities/schedules */
export interface ScheduleList {
  items: FacilitySchedule[];
  permissions: { manage: boolean; open: boolean };
}

/** POST /facilities/schedules, PUT /facilities/schedules/:id */
export interface ScheduleInput {
  kind: FacilityScheduleKind;
  name: string;
  description?: string | null;
  enabled: boolean;
  facilityAssetId?: string | null;
  buildingId?: string | null;
  roomId?: string | null;
  departmentId?: string | null;
  priority: WorkOrderPriority;
  checklist: string[];
  estimatedMinutes?: number | null;
  templateId?: string | null;
  assignedToId?: string | null;
  frequency: RecurrenceFrequency;
  interval: number;
  weekdays: number[];
  dayOfMonth?: number | null;
  month?: number | null;
  startDate: string;
  endDate?: string | null;
  leadDays: number;
}

/** POST /facilities/schedules/preview */
export interface SchedulePreview {
  recurrence: string;
  dates: string[];
}

/** GET /facilities/due */
export interface DueItem {
  /** UPCOMING: an occurrence a schedule hasn't opened yet (id is the schedule's). */
  type: 'WORK_ORDER' | 'INSPECTION' | 'UPCOMING';
  id: string;
  number: number | null;
  title: string;
  status: string | null;
  priority: WorkOrderPriority | null;
  dueOn: string;
  opensOn: string | null;
  overdue: boolean;
  building: Ref | null;
  room: Ref | null;
  facilityAsset: EquipmentRef | null;
  assignedTo: Ref | null;
  schedule: Ref | null;
  /** UPCOMING: the schedule's last occurrence, still open. */
  waitingOn: ScheduleOccurrence | null;
}

export interface DueWork {
  today: string;
  until: string;
  items: DueItem[];
}

// ---------------------------------------------------------------------------
// Inspections
// ---------------------------------------------------------------------------

export interface InspectionItem {
  id: string;
  section: string;
  label: string;
  guidance: string | null;
}

/** GET /facilities/inspection-templates */
export interface InspectionTemplate {
  id: string;
  name: string;
  kind: InspectionKind;
  description: string | null;
  items: InspectionItem[];
  createFollowUps: boolean;
  followUpDepartmentId: string | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  followUpCategory: string | null;
  scheduleCount: number;
  inspectionCount: number;
}

/** POST /facilities/inspection-templates, PUT /facilities/inspection-templates/:id */
export interface InspectionTemplateInput {
  name: string;
  kind: InspectionKind;
  description?: string | null;
  /** Items without an id get one; keep ids when editing so past results still line up. */
  items: { id?: string; section: string; label: string; guidance?: string | null }[];
  createFollowUps: boolean;
  followUpDepartmentId?: string | null;
  archived: boolean;
}

interface InspectionBase {
  id: string;
  templateId: string | null;
  name: string;
  kind: InspectionKind;
  status: InspectionStatus;
  buildingId: string | null;
  roomId: string | null;
  facilityAssetId: string | null;
  scheduleId: string | null;
  inspectorId: string | null;
  dueOn: string | null;
  startedAt: string | null;
  completedAt: string | null;
  completedById: string | null;
  completedByName: string | null;
  /** Percent of the items that applied which passed. */
  score: number | null;
  passedCount: number;
  failedCount: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  building: Ref | null;
  room: Ref | null;
  facilityAsset: EquipmentRef | null;
  inspector: Ref | null;
  schedule: Ref | null;
  overdue: boolean;
}

/** GET /facilities/inspections (paged) */
export interface InspectionListItem extends InspectionBase {
  itemCount: number;
  answeredCount: number;
  followUpCount: number;
}

export interface InspectionDetailItem extends InspectionItem {
  result: InspectionResult | null;
  note: string | null;
  workOrder: { id: string; number: number; title: string; status: WorkOrderStatus } | null;
}

/** GET /facilities/inspections/:id */
export interface InspectionDetail extends InspectionBase {
  template: { id: string; name: string; createFollowUps: boolean } | null;
  items: InspectionDetailItem[];
  followUps: { id: string; number: number; title: string; status: WorkOrderStatus; assignedTo: Ref | null }[];
  documents: DocumentSummary[];
  permissions: { respond: boolean; edit: boolean; delete: boolean };
}

// ---------------------------------------------------------------------------
// Work order checklists and costs
// ---------------------------------------------------------------------------

export interface WorkOrderCost {
  id: string;
  kind: WorkOrderCostKind;
  description: string;
  vendor: string | null;
  amount: number;
  incurredOn: string;
  createdById: string | null;
  createdByName: string;
  createdAt: string;
}

/** GET /work-orders/:id/costs */
export interface WorkOrderCosts {
  costs: WorkOrderCost[];
  labor: { minutes: number; hourlyRate: number | null; cost: number | null };
  totals: { other: number; total: number };
  permissions: { add: boolean; deleteAny: boolean };
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export interface FacilityDocument extends DocumentSummary {
  description: string | null;
  tags: string[];
  facilityAssetId: string | null;
  buildingId: string | null;
  inspectionId: string | null;
  workOrderId: string | null;
  uploadedById: string | null;
  uploadedByName: string;
  updatedAt: string;
  facilityAsset: EquipmentRef | null;
  building: Ref | null;
  inspection: Ref | null;
  workOrder: { id: string; number: number; title: string } | null;
}

/** GET /facilities/documents */
export interface DocumentPage extends Page<FacilityDocument> {
  permissions: { upload: boolean; deleteAny: boolean };
}

export interface DocumentInput {
  title: string;
  kind?: FacilityDocumentKind;
  description?: string | null;
  tags?: string[];
  facilityAssetId?: string | null;
  buildingId?: string | null;
  inspectionId?: string | null;
  workOrderId?: string | null;
}

export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

/** Types the browser may open in a tab; everything else downloads. */
export const INLINE_DOCUMENT_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp']);

/** Uploads a document: the file is the request body, its details go in a header. */
export async function uploadDocument(file: File, input: DocumentInput) {
  const { description, ...details } = input;
  const document = await upload<FacilityDocument>('/facilities/documents', file, {
    'x-document': encodeURIComponent(JSON.stringify({ ...details, fileName: file.name, contentType: file.type || undefined })),
  });
  // A long description is saved separately, so the header stays small.
  return description ? api.patch<FacilityDocument>(`/facilities/documents/${document.id}`, { description }) : document;
}

/** A link that downloads the document, or opens it in the browser when that's safe. */
export const documentUrl = (document: { id: string; contentType: string }, open = false) =>
  apiUrl(`/facilities/documents/${document.id}/download`, { inline: open && INLINE_DOCUMENT_TYPES.has(document.contentType) ? true : undefined });

// ---------------------------------------------------------------------------
// Routing rules
// ---------------------------------------------------------------------------

/** GET /facilities/routing-rules */
export interface RoutingRule {
  id: string;
  name: string;
  enabled: boolean;
  sortOrder: number;
  departmentId: string | null;
  buildingId: string | null;
  roomId: string | null;
  assignment: RoutingAssignment;
  assignedToId: string | null;
  teamId: string | null;
  createdAt: string;
  updatedAt: string;
  building: Ref | null;
  room: Ref | null;
  assignedTo: Ref | null;
  category: { id: string; path: string } | null;
  team: { id: string; path: string } | null;
  matchesEverything: boolean;
}

export interface RoutingRuleInput {
  name: string;
  enabled: boolean;
  departmentId?: string | null;
  buildingId?: string | null;
  roomId?: string | null;
  assignment: RoutingAssignment;
  assignedToId?: string | null;
  teamId?: string | null;
}

/** POST /facilities/routing-rules/preview */
export interface RoutingPreview {
  rule: Ref | null;
  assignee: { id: string; name: string; note: string | null } | null;
  passedOver: Ref[];
}

// ---------------------------------------------------------------------------
// Dashboard and reports
// ---------------------------------------------------------------------------

export interface WorkloadTally {
  open: number;
  overdue: number;
  urgent: number;
  inProgress: number;
  unassigned: number;
  oldestOpenDays: number | null;
}

/** GET /facilities/dashboard */
export interface FacilitiesDashboard {
  today: string;
  totals: WorkloadTally & { waiting: number; dueToday: number; createdThisWeek: number; resolvedThisWeek: number };
  technicians: (WorkloadTally & { userId: string; name: string; away: boolean; onTeam: boolean })[];
  buildings: (WorkloadTally & { buildingId: string | null; name: string })[];
  categories: (WorkloadTally & { departmentId: string | null; path: string })[];
  aging: { label: string; count: number }[];
  maintenance: {
    preventiveOpen: number;
    preventiveOverdue: number;
    followUpsOpen: number;
    inspectionsOpen: number;
    inspectionsDueToday: number;
    inspectionsOverdue: number;
    schedulesDueSoon: number;
    failedInspections30Days: number;
  };
  bottlenecks: { kind: 'TECHNICIAN' | 'BUILDING' | 'CATEGORY' | 'QUEUE'; id: string | null; message: string }[];
}

interface InspectionGroupRow {
  completed: number;
  averageScore: number | null;
  failedItems: number;
  withFailures: number;
}

/** GET /facilities/reports/maintenance */
export interface MaintenanceReport {
  days: number;
  preventive: {
    due: number;
    onTime: number;
    late: number;
    stillOpen: number;
    complianceRate: number | null;
    skippedAllTime: number;
    bySchedule: { scheduleId: string | null; name: string; due: number; onTime: number; late: number; open: number; skippedAllTime: number; complianceRate: number | null }[];
    overdue: { id: string; number: number; title: string; status: WorkOrderStatus; dueAt: string; assignedTo: Ref | null; building: string | null }[];
  };
  inspections: InspectionGroupRow & {
    followUpsOpened: number;
    followUpsResolved: number;
    overdueNow: number;
    byKind: (InspectionGroupRow & { kind: InspectionKind })[];
    byTemplate: (InspectionGroupRow & { templateId: string | null; name: string })[];
    byBuilding: (InspectionGroupRow & { buildingId: string | null; name: string })[];
  };
  trend: { key: string; label: string; due: number; onTime: number; inspections: number; complianceRate: number | null; averageScore: number | null }[];
}

export interface CostRow {
  key: string;
  label: string;
  labor: number;
  other: number;
  total: number;
  minutes: number;
  workOrders: number;
}

/** GET /facilities/reports/costs */
export interface CostReport {
  days: number;
  hourlyRate: number | null;
  totals: Record<CostKind, number> & { laborMinutes: number; total: number };
  byBuilding: CostRow[];
  byCategory: CostRow[];
  bySystem: (CostRow & { system: FacilitySystem | null })[];
  byEquipment: (CostRow & { replacementCost: number | null })[];
  trend: { key: string; label: string; labor: number; other: number; total: number }[];
}

export interface ForecastEquipment extends Lifecycle {
  id: string;
  code: string | null;
  name: string;
  system: FacilitySystem;
  status: EquipmentStatus;
  condition: FacilityCondition;
  building: Ref | null;
  installedOn: string | null;
  expectedLifeYears: number | null;
  replacementCost: number | null;
  /** Spent on it so far: labor, materials, contractors, and recorded costs. */
  lifetimeCost: number;
  /** lifetimeCost as a percent of replacementCost. */
  costRatio: number | null;
  pastDue: boolean;
  needsAttention: boolean;
}

/** GET /facilities/reports/replacement */
export interface ReplacementForecast {
  today: string;
  horizonYears: number;
  totals: { inService: number; withLifecycle: number; missingLifecycle: number; replacementValue: number; needsAttention: number };
  pastDue: { count: number; cost: number };
  byYear: { year: number; count: number; cost: number; missingCost: number }[];
  assets: ForecastEquipment[];
}

// ---------------------------------------------------------------------------
// Labels and tones
// ---------------------------------------------------------------------------

type Tone = 'gray' | 'green' | 'blue' | 'amber' | 'red' | 'violet' | 'brand' | 'slate';

export const FACILITIES_ROLE_LABEL: Record<TenantRole, string> = {
  ADMIN: 'Facilities admin',
  TECHNICIAN: 'Facilities technician',
  VIEWER: 'Facilities viewer',
  REQUESTER: 'Can send requests',
};

export const FACILITIES_ROLE_DESCRIPTION: Record<FacilitiesRole, string> = {
  ADMIN: 'Everything technicians can do, plus routing rules, schedules, inspection templates, categories and crews, and the facilities mailbox',
  TECHNICIAN: 'Work facilities requests, log time and costs, keep equipment records, do inspections, and upload documents',
  VIEWER: 'See the facilities queue, equipment, schedules, inspections, documents, and reports',
};

export const FACILITY_SYSTEM_LABEL: Record<FacilitySystem, string> = {
  HVAC: 'Heating and cooling',
  ELECTRICAL: 'Electrical',
  PLUMBING: 'Plumbing',
  ROOFING: 'Roofing',
  FIRE_LIFE_SAFETY: 'Fire and life safety',
  SECURITY: 'Security',
  ELEVATOR: 'Elevators',
  KITCHEN: 'Kitchen',
  PLAYGROUND: 'Playground',
  GROUNDS: 'Grounds',
  STRUCTURAL: 'Structural',
  FURNISHINGS: 'Furnishings',
  OTHER: 'Other',
};

export const EQUIPMENT_STATUS_LABEL: Record<EquipmentStatus, string> = {
  IN_SERVICE: 'In service',
  NEEDS_REPAIR: 'Needs repair',
  OUT_OF_SERVICE: 'Out of service',
  RETIRED: 'Retired',
};
export const EQUIPMENT_STATUS_TONE: Record<EquipmentStatus, Tone> = { IN_SERVICE: 'green', NEEDS_REPAIR: 'amber', OUT_OF_SERVICE: 'red', RETIRED: 'gray' };

export const CONDITION_LABEL: Record<FacilityCondition, string> = { EXCELLENT: 'Excellent', GOOD: 'Good', FAIR: 'Fair', POOR: 'Poor', CRITICAL: 'Critical' };
export const CONDITION_TONE: Record<FacilityCondition, Tone> = { EXCELLENT: 'green', GOOD: 'green', FAIR: 'amber', POOR: 'red', CRITICAL: 'red' };

export const HISTORY_KIND_LABEL: Record<FacilityLogKind, string> = {
  MAINTENANCE: 'Maintenance',
  REPAIR: 'Repair',
  INSPECTION: 'Inspection',
  REPLACEMENT: 'Replacement',
  INSTALLATION: 'Installation',
  NOTE: 'Note',
};

export const SCHEDULE_KIND_LABEL: Record<FacilityScheduleKind, string> = { PREVENTIVE_MAINTENANCE: 'Preventive maintenance', INSPECTION: 'Inspection' };

export const SCHEDULE_STATE_LABEL: Record<ScheduleState, string> = {
  PAUSED: 'Paused',
  OVERDUE: 'Overdue',
  OPEN: 'In progress',
  ENDED: 'Ended',
  OPENING: 'Opening soon',
  SCHEDULED: 'Scheduled',
};
export const SCHEDULE_STATE_TONE: Record<ScheduleState, Tone> = { PAUSED: 'gray', OVERDUE: 'red', OPEN: 'blue', ENDED: 'gray', OPENING: 'amber', SCHEDULED: 'green' };

export const INSPECTION_KIND_LABEL: Record<InspectionKind, string> = {
  CUSTODIAL_WALKTHROUGH: 'Custodial walkthrough',
  SAFETY_AUDIT: 'Safety audit',
  PLAYGROUND: 'Playground inspection',
  FIRE_SAFETY: 'Fire safety',
  OTHER: 'Other',
};

export const INSPECTION_STATUS_LABEL: Record<InspectionStatus, string> = { SCHEDULED: 'Scheduled', IN_PROGRESS: 'In progress', COMPLETED: 'Completed', CANCELED: 'Canceled' };
export const INSPECTION_STATUS_TONE: Record<InspectionStatus, Tone> = { SCHEDULED: 'blue', IN_PROGRESS: 'amber', COMPLETED: 'green', CANCELED: 'gray' };

export const INSPECTION_RESULT_LABEL: Record<InspectionResult, string> = { PASS: 'Pass', FAIL: 'Fail', NOT_APPLICABLE: 'Not applicable' };

export const COST_KIND_LABEL: Record<CostKind, string> = {
  LABOR: 'Labor',
  MATERIALS: 'Materials and parts',
  CONTRACTOR: 'Contractor',
  EQUIPMENT: 'Equipment rental',
  OTHER: 'Other',
  RECORDED: 'Recorded in equipment history',
};

export const DOCUMENT_KIND_LABEL: Record<FacilityDocumentKind, string> = {
  MANUAL: 'Manual',
  WARRANTY: 'Warranty',
  MAINTENANCE_LOG: 'Maintenance log',
  INSPECTION_REPORT: 'Inspection report',
  CONTRACT: 'Contract',
  PERMIT: 'Permit or certificate',
  DRAWING: 'Drawing or plan',
  OTHER: 'Other',
};

export const ROUTING_ASSIGNMENT_LABEL: Record<RoutingAssignment, string> = {
  USER: 'A specific person',
  ROUND_ROBIN: 'The next available person in a crew',
  TEAM_LEAD: 'The crew’s lead',
};

export const FACILITY_SOURCE_LABEL: Record<WorkOrderSource, string> = {
  REQUESTER: 'Request',
  EMAIL: 'Email',
  STAFF: 'Logged by the team',
  SMART_RULE: 'Smart rule',
  RECURRING: 'Schedule',
  PREVENTIVE: 'Preventive maintenance',
  INSPECTION: 'Inspection follow-up',
};

export const scoreTone = (score: number | null): Tone => (score === null ? 'gray' : score >= 90 ? 'green' : score >= 75 ? 'amber' : 'red');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "RTU-03 · Rooftop unit 3" */
export const equipmentLabel = (equipment: { code: string | null; name: string }) => [equipment.code, equipment.name].filter(Boolean).join(' · ');

/** "Room 101, Main Hall · north stairwell" */
export function placeLabel(place: { building?: { name: string } | null; room?: { name: string } | null; locationDetail?: string | null }) {
  const where = [place.room?.name, place.building?.name].filter(Boolean).join(', ');
  return [where, place.locationDetail].filter(Boolean).join(' · ');
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

/** "3.5 years", "Past due by 2 years" */
export function describeRemainingLife(remainingYears: number | null) {
  if (remainingYears === null) return null;
  const years = Math.abs(remainingYears);
  const text = `${years} year${years === 1 ? '' : 's'}`;
  return remainingYears < 0 ? `Past expected life by ${text}` : `${text} left`;
}

// ---------------------------------------------------------------------------
// Shared queries (keys start with 'facilities', so invalidating ['facilities'] refreshes them all)
// ---------------------------------------------------------------------------

export const useFacilityPlaces = (enabled = true) =>
  useQuery({ queryKey: ['facilities', 'places'], queryFn: () => api.get<FacilityPlace[]>('/facilities/places'), enabled, staleTime: 60_000 });

export const useEquipmentOptions = (buildingId?: string | null, enabled = true) =>
  useQuery({
    queryKey: ['facilities', 'equipment-options', buildingId || 'all'],
    queryFn: () => api.get<EquipmentOption[]>('/facilities/equipment-options', { buildingId: buildingId || undefined }),
    enabled,
    staleTime: 60_000,
  });

export const useInspectionTemplates = (includeArchived = false, enabled = true) =>
  useQuery({
    queryKey: ['facilities', 'inspection-templates', includeArchived],
    queryFn: () => api.get<InspectionTemplate[]>('/facilities/inspection-templates', { includeArchived: includeArchived || undefined }),
    enabled,
    staleTime: 60_000,
  });
